/**
 * IconSection - Station icon management section
 * Single square image (256-1024px), max 2MB
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Chip,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { ImageSectionProps, IMAGE_TYPE_CONFIGS, CropModalState, INITIAL_CROP_STATE } from './types';
import ImageUploadDropzone from './ImageUploadDropzone';
import ImageCard from './ImageCard';
import ImageCropModal from './ImageCropModal';

const IconSection: React.FC<ImageSectionProps> = ({
  stationId,
  currentImage,
  pendingImage,
  rejectedImage,
  uploadingImage,
  onUpload,
  onDelete,
  disabled = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cropModal, setCropModal] = useState<CropModalState>(INITIAL_CROP_STATE);

  const config = IMAGE_TYPE_CONFIGS.icon;
  const hasImage = !!currentImage || !!uploadingImage;
  const hasPendingImage = pendingImage?.status === 'pending_review';
  const isUploading = uploadingImage?.status === 'uploading' || uploadingImage?.status === 'confirming';

  const handleFileSelect = async (files: File[]) => {
    if (files.length > 0 && !disabled && !readOnly) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setCropModal({
        open: true,
        file,
        previewUrl,
      });
    }
  };

  const handleCropComplete = async (processedFile: File) => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);
    await onUpload(processedFile);
  };

  const handleCropCancel = () => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);
  };

  const handleReplaceClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (currentImage) {
      await onDelete(currentImage.id);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {config.label}
            </Typography>
            {hasPendingImage && (
              <Chip
                icon={<HourglassEmptyIcon />}
                label="Pending Review"
                size="small"
                color="warning"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>
        </Box>
      </Stack>

      {/* Pending Review Alert */}
      {hasPendingImage && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A new icon is awaiting admin approval. The current icon will remain active until the new one is approved.
        </Alert>
      )}

      {/* Rejected Image Alert */}
      {rejectedImage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Icon image was rejected
          </Typography>
          <Typography variant="body2">
            <strong>Reason:</strong> {rejectedImage.rejection_reason || 'No reason provided'}
          </Typography>
          {rejectedImage.rejected_by && (
            <Typography variant="body2">
              <strong>Reviewed by:</strong> {rejectedImage.rejected_by}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Please upload a new icon image.
          </Typography>
        </Alert>
      )}

      {/* Content */}
      <Stack direction="row" spacing={3} alignItems="flex-start">
        {/* Preview Area */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: 2,
            overflow: 'hidden',
            border: hasImage ? 'none' : `2px dashed ${theme.palette.divider}`,
            bgcolor: hasImage ? 'transparent' : alpha(theme.palette.grey[500], 0.04),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {hasImage ? (
            <ImageCard
              image={currentImage || undefined}
              uploadingImage={uploadingImage}
              aspectRatio={1}
              onPreview={currentImage ? () => setShowPreview(true) : undefined}
              onDelete={!readOnly && currentImage ? handleDeleteClick : undefined}
              disabled={disabled || isUploading}
            />
          ) : (
            <Stack alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                  width: 48,
                  height: 48,
                }}
              >
                <ImageIcon sx={{ color: 'text.secondary' }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                No icon
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Upload/Replace Area */}
        {!readOnly && (
          <Box sx={{ flex: 1, maxWidth: 300 }}>
            {hasImage ? (
              // Replace button when image exists
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleReplaceClick}
                  disabled={disabled || isUploading}
                  fullWidth
                >
                  Replace Icon
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {config.description}
                </Typography>
              </Stack>
            ) : (
              // Dropzone when no image
              <ImageUploadDropzone
                onDrop={handleFileSelect}
                accept={config.acceptTypes}
                maxSize={config.maxSize}
                disabled={disabled}
                label={`PNG, JPEG or WebP - Max ${config.maxSize / (1024 * 1024)}MB`}
              />
            )}
          </Box>
        )}
      </Stack>

      {/* Hidden file input for replace */}
      <input
        ref={fileInputRef}
        type="file"
        accept={config.acceptExtensions}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Station Icon Preview</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <Box
              component="img"
              src={currentImage?.url}
              alt="Station icon"
              sx={{
                maxWidth: '100%',
                maxHeight: 400,
                borderRadius: 2,
                boxShadow: theme.shadows[4],
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Icon?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this station icon? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crop Modal */}
      {cropModal.open && cropModal.file && cropModal.previewUrl && (
        <ImageCropModal
          open={cropModal.open}
          imageSrc={cropModal.previewUrl}
          imageType="icon"
          originalFile={cropModal.file}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </Box>
  );
};

export default IconSection;
